// src/pages/AIZoneExtended.ts
import "./AIZone.css";
import "./AIZoneExtended.css";
import { renderAIRichText } from "../components/AIRichText";
import { renderAIZoneSearchBar } from "../components/AIZoneSearchBar";
import { getClientTimeContext } from "../utils/clientTimeContext";
import {
  compactLocationHint,
  getFreshWeatherLocationContext,
  isNearMeQuery,
} from "../components/UserLocation";
import { mountCurrencyConverterData } from "../components/CurrencyConverter";
import { mountTodaysCurrencyData } from "../components/TodaysCurrency";
import type { CurrencyResult } from "../components/currency/CurrencyTypes";
import type { TranslationOverviewPayload } from "../components/AiOverviewTranslator";
import { renderDictionaryCard } from "../components/DictionaryCard";

interface ExtendedMessage {
  role: "user" | "ai";
  text: string;
  sources?: ExtendedSource[];
  currency?: CurrencyResult;
  showCurrencyComponent?: boolean;
  forceTodaysCurrencyComponent?: boolean;
  translation?: TranslationOverviewPayload;
  showDictionaryComponent?: boolean;
  translationDescription?: string;
}

interface ExtendedSource {
  title?: string;
  url?: string;
  domain?: string;
  snippet?: string;
}

export interface AIZoneExtendedSeed {
  query: string;
  answer: string;
  onClose?: () => void;
  sources?: ExtendedSource[];
  responseType?: string;
  currency?: CurrencyResult;
  showCurrencyComponent?: boolean;
  forceTodaysCurrencyComponent?: boolean;
  translation?: TranslationOverviewPayload;
  showDictionaryComponent?: boolean;
}

interface AIZoneExtendedApiResponse {
  answer?: string;
  error?: string;
  conversation_id?: string;
  sources?: ExtendedSource[];
  response_type?: string;
  deterministic?: boolean;
  currency?: CurrencyResult;
  translation?: TranslationOverviewPayload;
}

const API_URL = "https://api.ayesearch.com/api/ai/zone/";
const CONVERSATION_KEY = "ayesearch_ai_zone_conversation_id";

let activeOverlay: HTMLElement | null = null;
let messages: ExtendedMessage[] = [];
let closeCallback: (() => void) | undefined;
let currentShell: HTMLElement | null = null;

function escapeHtml(value: string): string {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function getConversationId(): string {
  try {
    return localStorage.getItem(CONVERSATION_KEY) || "";
  } catch {
    return "";
  }
}

function saveConversationId(value: string) {
  if (!value) return;
  try {
    localStorage.setItem(CONVERSATION_KEY, value);
  } catch {
    // Storage failure must never block the extended experience.
  }
}


function explicitCurrencyComponentRequest(query: string): boolean {
  return /\b(?:component|converter)\b/i.test(String(query || ""));
}


function explicitTodaysCurrencyComponentRequest(query: string): boolean {
  const q = String(query || "");
  return (
    /\b(?:today|today's|current|currently)\b/i.test(q) &&
    /\b(?:component|converter)\b/i.test(q)
  );
}

function splitTranslationOverviewText(value: string): {
  lead: string;
  description: string;
} {
  const clean = String(value || "").trim();
  if (!clean) {
    return { lead: "", description: "" };
  }

  const paragraphs = clean
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (paragraphs.length <= 1) {
    return { lead: clean, description: "" };
  }

  return {
    lead: paragraphs[0],
    description: paragraphs.slice(1).join("\n\n"),
  };
}

function extendedSourceLabel(source: ExtendedSource): string {
  return String(source.title || source.domain || source.url || "Source").trim();
}

function extendedSourceDomain(source: ExtendedSource): string {
  if (source.domain) return source.domain;
  try {
    return new URL(source.url || "").hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function extendedSourceFavicon(source: ExtendedSource): string {
  try {
    const url = new URL(source.url || "");
    return `${url.origin}/favicon.ico`;
  } catch {
    return "";
  }
}

function extendedValidSources(message: ExtendedMessage): ExtendedSource[] {
  return (message.sources || []).filter((source) => !!source.url);
}

function extendedSourceIcon(source: ExtendedSource, compact = false): string {
  const label = extendedSourceLabel(source);
  const favicon = extendedSourceFavicon(source);
  const klass = compact ? "aiz-source-avatar is-compact" : "aiz-source-avatar";
  if (!favicon) {
    return `<span class="${klass} aiz-source-avatar-letter">${escapeHtml(label.slice(0, 1).toUpperCase())}</span>`;
  }
  return `
    <span class="${klass}">
      <img
        src="${escapeHtml(favicon)}"
        alt=""
        loading="lazy"
        onerror="this.remove();this.parentElement?.classList.add('aiz-source-avatar-letter');this.parentElement.textContent='${escapeHtml(label.slice(0, 1).toUpperCase())}'"
      />
    </span>
  `;
}

function renderExtendedCompactSources(message: ExtendedMessage): string {
  const sources = extendedValidSources(message);
  if (!sources.length) return "";
  return `
    <div class="aiz-source-compact-row">
      <div class="aiz-source-avatar-stack">
        ${sources.slice(0, 4).map((source) => `
          <a
            class="aiz-source-avatar-link"
            href="${escapeHtml(source.url || "")}"
            target="_blank"
            rel="noopener noreferrer"
            title="${escapeHtml(extendedSourceLabel(source))}"
          >${extendedSourceIcon(source, true)}</a>
        `).join("")}
      </div>
      <span class="aiz-source-site-count">${sources.length} site${sources.length === 1 ? "" : "s"}</span>
    </div>
  `;
}

function renderExtendedFullSources(message: ExtendedMessage): string {
  const sources = extendedValidSources(message);
  if (!sources.length) return "";
  return `
    <div class="aiz-sources aiz-sources-active">
      <div class="aiz-sources-title">Sources</div>
      <div class="aiz-source-card-grid">
        ${sources.map((source) => `
          <a
            class="aiz-source-card"
            href="${escapeHtml(source.url || "")}"
            target="_blank"
            rel="noopener noreferrer"
          >
            ${extendedSourceIcon(source)}
            <span class="aiz-source-card-copy">
              <span class="aiz-source-title">${escapeHtml(extendedSourceLabel(source))}</span>
              <span class="aiz-source-domain">${escapeHtml(extendedSourceDomain(source))}</span>
            </span>
            <span class="aiz-source-open" aria-hidden="true">↗</span>
          </a>
        `).join("")}
      </div>
    </div>
  `;
}

function lastExtendedAIIndex(): number {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === "ai") return index;
  }
  return -1;
}

function renderMessages() {
  if (!activeOverlay) return;

  const feed = activeOverlay.querySelector<HTMLElement>(
    "[data-aizx-feed]",
  );
  if (!feed) return;

  const activeAIIndex = lastExtendedAIIndex();

  feed.innerHTML = messages
    .map(
      (message, index) => `
        <div class="ai-msg ${message.role}">
          ${
            message.role === "ai" &&
            index !== activeAIIndex &&
            extendedValidSources(message).length
              ? renderExtendedCompactSources(message)
              : ""
          }
          <div class="msg-content">
            ${
              message.role === "ai"
                ? renderAIRichText(message.text, { variant: "chat" })
                : escapeHtml(message.text).replace(/\n/g, "<br>")
            }
          </div>
          ${
            message.role === "ai" &&
            message.showCurrencyComponent &&
            message.currency
              ? `<div class="aiz-inline-currency-component" id="aizx-currency-${index}"></div>`
              : ""
          }
          ${
            message.role === "ai" &&
            message.showDictionaryComponent &&
            message.translationDescription
              ? `
                <section class="aiz-inline-translation-section aiz-inline-translation-description">
                  <div class="aiz-inline-translation-section-content">
                    ${renderAIRichText(
                      message.translationDescription,
                      { variant: "chat" },
                    )}
                  </div>
                </section>
              `
              : ""
          }
          ${
            message.role === "ai" &&
            message.showDictionaryComponent &&
            message.translation?.translation
              ? `<div class="aiz-inline-dictionary-component" id="aizx-dictionary-${index}"></div>`
              : ""
          }
          ${
            message.role === "ai" &&
            message.showDictionaryComponent &&
            message.translation?.expanded_details
              ? `<div class="aiz-inline-dictionary-details">${renderAIRichText(
                  message.translation.expanded_details,
                  { variant: "chat" },
                )}</div>`
              : ""
          }
          ${
            message.role === "ai" &&
            index === activeAIIndex &&
            extendedValidSources(message).length
              ? renderExtendedFullSources(message)
              : ""
          }
        </div>
      `,
    )
    .join("");

  messages.forEach((message, index) => {
    if (
      message.role !== "ai" ||
      !message.showCurrencyComponent ||
      !message.currency
    ) {
      return;
    }

    const host = activeOverlay?.querySelector<HTMLElement>(
      `#aizx-currency-${index}`,
    );
    if (!host) return;

    const mount =
      message.forceTodaysCurrencyComponent || message.currency.mode === "today"
        ? mountTodaysCurrencyData(host, {
            ...message.currency,
            mode: "today",
            amount: "1",
          })
        : mountCurrencyConverterData(host, message.currency);
    void mount;
  });

  messages.forEach((message, index) => {
    if (
      message.role !== "ai" ||
      !message.showDictionaryComponent ||
      !message.translation?.translation
    ) {
      return;
    }

    const host = activeOverlay?.querySelector<HTMLElement>(
      `#aizx-dictionary-${index}`,
    );
    if (host) {
      void renderDictionaryCard(host, message.translation.translation);
    }
  });

  feed.scrollTop = feed.scrollHeight;
}

function finishClose() {
  const overlay = activeOverlay;
  activeOverlay = null;
  currentShell = null;

  overlay?.remove();
  document.body.classList.remove("aizx-open");

  closeCallback?.();
  closeCallback = undefined;
}

function destroyOverlay() {
  if (!activeOverlay) return;

  activeOverlay.classList.add("is-closing");

  if (currentShell) {
    currentShell.style.transition =
      "transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)";
    currentShell.style.transform = "translateY(100%)";
  }

  window.setTimeout(finishClose, 180);
}

async function sendMessage(query: string) {
  const clean = String(query || "").trim();
  if (!clean || !activeOverlay) return;

  messages.push({ role: "user", text: clean });
  renderMessages();

  const feed = activeOverlay.querySelector<HTMLElement>(
    "[data-aizx-feed]",
  );

  const loading = document.createElement("div");
  loading.className = "ai-msg ai";
  loading.innerHTML = `
    <div class="typing-indicator">
      Thinking<span class="dots"></span>
    </div>
  `;
  feed?.appendChild(loading);

  if (feed) {
    feed.scrollTop = feed.scrollHeight;
  }

  try {
    const {
      timezone,
      locale,
      timezoneOffsetMinutes,
    } = getClientTimeContext();

    const weatherLocationContext = isNearMeQuery(clean)
      ? await getFreshWeatherLocationContext()
      : null;

    const response = await fetch(API_URL, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        q: clean,
        conversation_id: getConversationId() || undefined,
        timezone: timezone || undefined,
        timezone_offset_minutes: timezoneOffsetMinutes,
        locale: locale || undefined,
        weather_location: weatherLocationContext
          ? JSON.stringify(
              compactLocationHint(
                weatherLocationContext.location,
              ),
            )
          : undefined,
        weather_display_location:
          weatherLocationContext?.displayLabel || undefined,
      }),
    });

    const data =
      (await response.json()) as AIZoneExtendedApiResponse;

    loading.remove();

    if (!response.ok) {
      messages.push({
        role: "ai",
        text:
          data.answer ||
          data.error ||
          `AI Zone returned HTTP ${response.status}.`,
      });
      renderMessages();
      return;
    }

    if (data.conversation_id) {
      saveConversationId(data.conversation_id);
    }

    messages.push({
      role: "ai",
      text:
        typeof data.answer === "string" &&
        data.answer.trim()
          ? data.answer
          : "Sorry, I couldn't generate a response.",
      sources: Array.isArray(data.sources) ? data.sources : [],
      currency: data.currency,
      showCurrencyComponent:
        String(data.response_type || "").toLowerCase() === "currency" &&
        !!data.currency &&
        explicitCurrencyComponentRequest(clean),
      forceTodaysCurrencyComponent:
        String(data.response_type || "").toLowerCase() === "currency" &&
        !!data.currency &&
        explicitTodaysCurrencyComponentRequest(clean),
      translation: data.translation,
    });

    renderMessages();
  } catch (error) {
    console.error(
      "Extended AI Zone request failed:",
      error,
    );

    loading.remove();

    messages.push({
      role: "ai",
      text:
        "Error connecting to AI. Please try again later.",
    });

    renderMessages();
  }
}

/**
 * Make the entire top bar draggable.
 *
 * Important:
 * - Drag starts from the handle OR empty/center header space.
 * - Drag does not start from the X button.
 * - Pointer Events + pointer capture make this reliable on touch,
 *   mouse, embedded mobile browsers and desktop touch emulation.
 */
function setupDragToClose(
  shell: HTMLElement,
  dragSurface: HTMLElement,
) {
  let pointerId: number | null = null;
  let startY = 0;
  let lastY = 0;
  let dragging = false;

  const clearDrag = () => {
    dragging = false;
    pointerId = null;
    shell.classList.remove("is-dragging");
  };

  const snapBack = () => {
    shell.style.transition =
      "transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)";
    shell.style.transform = "translateY(0)";

    window.setTimeout(() => {
      shell.style.transition = "";
      shell.style.transform = "";
      clearDrag();
    }, 190);
  };

  const start = (event: PointerEvent) => {
    const target = event.target as HTMLElement | null;

    if (
      target?.closest("[data-aizx-close]") ||
      target?.closest("button") ||
      target?.closest("a") ||
      target?.closest("input") ||
      target?.closest("textarea")
    ) {
      return;
    }

    pointerId = event.pointerId;
    startY = event.clientY;
    lastY = event.clientY;
    dragging = true;

    shell.classList.add("is-dragging");
    shell.style.transition = "none";

    dragSurface.setPointerCapture?.(event.pointerId);
  };

  const move = (event: PointerEvent) => {
    if (
      !dragging ||
      pointerId !== event.pointerId
    ) {
      return;
    }

    lastY = event.clientY;
    const delta = Math.max(0, lastY - startY);

    shell.style.transform =
      `translateY(${delta}px)`;

    // Once movement is clearly intentional, stop page gestures
    // from taking over the sheet drag.
    if (delta > 6) {
      event.preventDefault();
    }
  };

  const finish = (event: PointerEvent) => {
    if (
      !dragging ||
      pointerId !== event.pointerId
    ) {
      return;
    }

    const delta = Math.max(0, lastY - startY);

    try {
      dragSurface.releasePointerCapture?.(
        event.pointerId,
      );
    } catch {
      // Ignore capture release failures.
    }

    if (delta >= 72) {
      shell.style.transition =
        "transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)";
      shell.style.transform = "translateY(100%)";

      window.setTimeout(finishClose, 180);
      return;
    }

    snapBack();
  };

  dragSurface.addEventListener(
    "pointerdown",
    start,
  );
  dragSurface.addEventListener(
    "pointermove",
    move,
    { passive: false },
  );
  dragSurface.addEventListener(
    "pointerup",
    finish,
  );
  dragSurface.addEventListener(
    "pointercancel",
    finish,
  );
}

export function openAIZoneExtended(
  seed: AIZoneExtendedSeed,
) {
  // Product behavior is mobile-only.
  if (
    !window.matchMedia(
      "(max-width: 768px)",
    ).matches
  ) {
    return;
  }

  activeOverlay?.remove();

  const translationSeed =
    seed.responseType === "translation"
      ? splitTranslationOverviewText(seed.answer)
      : { lead: seed.answer.trim(), description: "" };

  messages = [
    ...(seed.query.trim()
      ? [
          {
            role: "user" as const,
            text: seed.query.trim(),
          },
        ]
      : []),
    {
      role: "ai",
      text:
        translationSeed.lead ||
        "AI Overview",
      translationDescription:
        seed.responseType === "translation"
          ? translationSeed.description
          : "",
      sources: Array.isArray(seed.sources) ? seed.sources : [],
      currency: seed.currency,
      // Show more from Search Results is the intentional one-time component
      // handoff into AI Zone. Subsequent direct prompts revert to text-only
      // unless the user explicitly asks for a converter/component.
      showCurrencyComponent:
        seed.responseType === "currency" &&
        !!seed.currency &&
        seed.showCurrencyComponent === true,
      forceTodaysCurrencyComponent:
        seed.responseType === "currency" &&
        seed.currency?.mode === "today",
      translation: seed.translation,
      showDictionaryComponent:
        seed.responseType === "translation" &&
        !!seed.translation,
    },
  ];

  closeCallback = seed.onClose;

  const overlay =
    document.createElement("div");

  overlay.className = "aizx-overlay";

  overlay.innerHTML = `
    <section
      class="aizx-shell ai-wrapper"
      aria-label="Expanded AI Overview"
    >
      <div class="ai-main aizx-main">
        <header
          class="ai-topnav aizx-topnav"
          data-aizx-drag-surface
        >
          <div class="aizx-topnav-left">
            <div
              class="aizx-drag-handle"
              aria-hidden="true"
            >
              <span></span>
            </div>
          </div>

          <div class="aizx-topnav-center">
            AI Zone
          </div>

          <div class="aizx-topnav-right">
            <button
              type="button"
              class="aizx-close"
              data-aizx-close
              aria-label="Close expanded AI Overview"
              title="Close"
            >
              ×
            </button>
          </div>
        </header>

        <main
          class="ai-chat-feed aizx-feed"
          id="aizx-chat-feed"
          data-aizx-feed
        ></main>

        <footer class="ai-chat-input aizx-chat-input">
          <div
            id="aizx-searchbar-container"
            class="aizx-searchbar-container"
          ></div>
        </footer>
      </div>
    </section>
  `;

  document.body.appendChild(overlay);
  document.body.classList.add(
    "aizx-open",
  );

  activeOverlay = overlay;

  const shell =
    overlay.querySelector<HTMLElement>(
      ".aizx-shell",
    );

  const dragSurface =
    overlay.querySelector<HTMLElement>(
      "[data-aizx-drag-surface]",
    );

  if (!shell || !dragSurface) {
    finishClose();
    return;
  }

  currentShell = shell;

  renderMessages();

  // Use the REAL AI Zone search bar so typography, spacing,
  // textarea growth, plus icon and send button match exactly.
  const searchContainer =
    overlay.querySelector<HTMLElement>(
      "#aizx-searchbar-container",
    );

  if (searchContainer) {
    renderAIZoneSearchBar(
      searchContainer,
      sendMessage,
    );
  }

  setupDragToClose(
    shell,
    dragSurface,
  );

  overlay
    .querySelector(
      "[data-aizx-close]",
    )
    ?.addEventListener(
      "click",
      destroyOverlay,
    );

  requestAnimationFrame(() => {
    overlay.classList.add("is-open");
  });
}
